import React, { useState, useRef, useEffect, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import "./DirectionsTooltip.css";

const SOMALI_DIRECTIONS = `Xafiiskeenna cusub wuxuu ku yaallaa dhabarka dambe ee xafiiskeennii hore.

Sida aad nagu soo gaadhi karto:
Marka aad joogto xafiiskeennii hore, dhexmar maqaaxida ‘Ha Idhaafin Restaurant’. Marka aad ka baxdo maqaaxida, midig u leexo, ka dibna eeg dhinacaaga midig. Waxaad halkaas ka heli doontaa xafiiskeenna cusub ee Suite #31, oo ah Old Karmel Mall.`;

const MARGIN = 12;
const EST_HEIGHT = 200;

/**
 * Small inline "i" trigger that reveals directions to our Karmel Mall
 * office in Somali — helps clients who are used to our previous office.
 */
const DirectionsTooltip = ({
  className = "",
  label = "Tilmaamo ku socda xafiiska (Somali directions)",
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const tooltipId = useId();

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el || typeof window === "undefined") return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - MARGIN * 2);
    const half = width / 2;

    let placement = "bottom";
    let top = rect.bottom + 10;
    if (top + EST_HEIGHT > window.innerHeight - MARGIN) {
      placement = "top";
      top = rect.top - 10;
    }

    let left = rect.left + rect.width / 2;
    left = Math.min(
      Math.max(left, half + MARGIN),
      window.innerWidth - half - MARGIN,
    );

    setCoords({ top, left, width, placement });
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setOpen(true);
  }, [updatePosition]);

  const hide = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        popoverRef.current?.contains(e.target)
      ) {
        return;
      }
      hide();
    };
    const handleKey = (e) => {
      if (e.key === "Escape") hide();
    };
    const handleReposition = () => updatePosition();

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, hide, updatePosition]);

  return (
    <span className={`dt-wrap ${className}`}>
      <button
        type="button"
        ref={triggerRef}
        className={`dt-trigger${open ? " dt-trigger-active" : ""}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          open ? hide() : show();
        }}
        aria-describedby={tooltipId}
        aria-label={label}
        aria-expanded={open}
      >
        <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            id={tooltipId}
            ref={popoverRef}
            role="tooltip"
            className={`dt-popover dt-popover-${coords.placement}`}
            style={{
              top: coords.top,
              left: coords.left,
              width: coords.width,
            }}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <button
              type="button"
              className="dt-popover-close"
              onClick={hide}
              aria-label="Xir (Close)"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
            <div className="dt-popover-header">
              <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
              <span>Sida aad noo heli karto</span>
            </div>
            <p className="dt-popover-body">{SOMALI_DIRECTIONS}</p>
          </div>,
          document.body,
        )}
    </span>
  );
};

export default DirectionsTooltip;
